// The learner's persistent lesson state: per-card scheduling, the chosen
// piece, and daily practice seconds toward the 15-minute goal. Storage is
// injected (localStorage in the app) so tests run against a plain object.

{ type Card, type PieceBias } from ./cards.civet
{ type PieceProgress } from ./progress.civet
{ type CardState, type Rating, isDue, newCardState, review } from ./srs.civet

export DAILY_GOAL_SECONDS := 15 * 60
export NEW_PER_SESSION := 8
export MAX_SESSION_CARDS := 40

export type Deck =
  version: number
  cards: Record<string, CardState>
  piece?: PieceBias
  // ISO date -> seconds practiced
  daily: Record<string, number>
  // per-piece section history (lessons/progress.civet), keyed by the
  // piece's identity — its metadata sourceMd5 when present, else title
  progress?: Record<string, PieceProgress>

export type DeckStorage =
  getItem: (key: string) => string?
  setItem: (key: string, value: string) => void

emptyDeck := (): Deck ->
  version: 1
  cards: {}
  daily: {}

export loadDeck := (storage: DeckStorage, key: string): Deck ->
  try
    if json := storage.getItem key
      return {...emptyDeck(), ...JSON.parse json}
  emptyDeck()

export saveDeck := (storage: DeckStorage, key: string, deck: Deck): void ->
  try
    storage.setItem key, JSON.stringify deck
  return

dateKey := (now: number) ->
  new Date(now).toISOString().slice 0, 10

export todaySeconds := (deck: Deck, now: number) ->
  deck.daily[dateKey now] ?? 0

export addPracticeSeconds := (deck: Deck, now: number, seconds: number): void ->
  k := dateKey now
  deck.daily[k] = (deck.daily[k] ?? 0) + seconds
  return

// Rotation for a session: everything due (oldest first), then new cards in
// the pool's introduction order, capped so a backlog stays a session
export sessionCards := (deck: Deck, pool: Card[], now: number): Card[] ->
  byId := new Map pool.map (card) -> [card.id, card]

  due := Object.keys deck.cards
    .filter (id) -> byId.has(id) and isDue deck.cards[id], now
    .sort (a, b) -> deck.cards[a].due - deck.cards[b].due
    .map (id) -> byId.get(id)!

  fresh := pool.filter (card) -> not (card.id in deck.cards)
    .slice 0, NEW_PER_SESSION

  [...due, ...fresh].slice 0, MAX_SESSION_CARDS

// Apply a finished session's ratings to the schedule
export recordResults := (deck: Deck, results: {id: string, rating: Rating}[], now: number): void ->
  results.forEach ({id, rating}) ->
    state := deck.cards[id] ?? newCardState now
    deck.cards[id] = review state, rating, now
  return

// Counts for the home screen
export deckCounts := (deck: Deck, pool: Card[], now: number) ->
  cards := sessionCards deck, pool, now
  due: cards.filter((card) -> card.id in deck.cards).length
  fresh: cards.filter((card) -> not (card.id in deck.cards)).length
